import { toast } from 'sonner';

export const UNDO_WINDOW_MS = 6000;

export type UndoableOptions = {
  /** Stable per object, so a second press on the same row replaces its toast instead of stacking. */
  readonly id: string;
  /** «Marco Bellini rimosso da DGBiotech» — said as already done, because on screen it is. */
  readonly message: string;
  readonly undoLabel: string;
  readonly countdown: (seconds: number) => string;
  /** The write. It runs only if the window expires without «Annulla». */
  readonly run: () => Promise<void>;
  readonly windowMs?: number;
};

/**
 * The full undo model (FR-UX-5, the screen standard's § Undoable deletion): the act shows at once,
 * the toast carries «Annulla» and a visible decrementing counter, and the WRITE is withheld until
 * the window expires — so «Annulla» cancels a request that was never sent, rather than racing to
 * reverse one that was. Used for «Rimuovi da qui», which revokes several grants at once, and for
 * «Disattiva», which nothing reverses but «Riattiva».
 *
 * Resolves `'undone'` or `'done'` once the outcome is known; a failing write rejects, and the
 * caller says so in plain words and restores what it hid.
 */
export function scheduleUndoable({
  id,
  message,
  undoLabel,
  countdown,
  run,
  windowMs = UNDO_WINDOW_MS,
}: UndoableOptions): Promise<'undone' | 'done'> {
  return new Promise((resolve, reject) => {
    let secondsLeft = Math.round(windowMs / 1000);
    let settled = false;

    const show = () =>
      toast(message, {
        id,
        description: countdown(secondsLeft),
        duration: secondsLeft * 1000 + 500,
        action: {
          label: undoLabel,
          onClick: () => {
            if (settled) {
              return;
            }
            settled = true;
            window.clearInterval(ticker);
            window.clearTimeout(fire);
            toast.dismiss(id);
            resolve('undone');
          },
        },
      });

    show();
    const ticker = window.setInterval(() => {
      secondsLeft -= 1;
      if (secondsLeft > 0) {
        show();
      }
    }, 1000);
    const fire = window.setTimeout(() => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearInterval(ticker);
      toast.dismiss(id);
      run().then(
        () => resolve('done'),
        (error: unknown) => reject(error instanceof Error ? error : new Error(String(error))),
      );
    }, windowMs);
  });
}
